// Persists table views (selected tab + each table's filter, sort and pagination) to
// sessionStorage, namespaced by view scope (Program Summary / Program Dashboard) and
// programId, so returning to a page restores the view the user drilled in from.
// Scopes keep each page's snapshot independent so they can't contaminate each other.
// Fresh top-level entry (Home / Navbar) or a reload clears everything via clearViewState().

const STORAGE_KEY = 'programSummaryViewState';

// View scopes keep each page's snapshot independent.
export const VIEW_SCOPE = {
  programSummary: 'programSummary',
  programDashboard: 'programDashboard',
};

const readAll = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const writeAll = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    // Persisting view state is best-effort; ignore quota/availability failures.
  }
};

// Returns the saved view state for a scope + program, or {} when none exists.
export const loadViewState = (scope, programId) => {
  if (!scope || !programId) return {};
  return readAll()[scope]?.[programId] || {};
};

// Merges a partial patch into the top-level view state for a scope + program (e.g. selectedTab).
export const saveViewState = (scope, programId, patch) => {
  if (!scope || !programId) return;
  const all = readAll();
  const scoped = all[scope] || {};
  scoped[programId] = { ...(scoped[programId] || {}), ...patch };
  all[scope] = scoped;
  writeAll(all);
};

// Merges a partial patch into one table's namespace ('variants' or 'patients').
export const saveTableViewState = (scope, programId, tableKey, patch) => {
  if (!scope || !programId) return;
  const all = readAll();
  const scoped = all[scope] || {};
  const program = scoped[programId] || {};
  program[tableKey] = { ...(program[tableKey] || {}), ...patch };
  scoped[programId] = program;
  all[scope] = scoped;
  writeAll(all);
};

// Clears all saved view state (used on fresh top-level entry / reload).
export const clearViewState = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Ignore storage errors; a stale snapshot is harmless.
  }
};
